import { basePath, calendarCsrfToken } from "./session.js";
import type {
  Appointment,
  AuditRecord,
  AuthMe,
  BulkScheduleOverridesInput,
  BulkScheduleOverridesResult,
  ClosureSeedName,
  Customer,
  DispatchStop,
  ExternalCalendarLink,
  Hold,
  Location,
  OperatorAuditRecord,
  PaginatedResponse,
  Proficiency,
  RecurrenceSeries,
  Resource,
  ResourceSkill,
  Schedule,
  Service,
  SyncConflict,
  TenantClosure,
  VerticalProfile,
  WebhookDelivery,
  WebhookSubscription
} from "./types.js";

/**
 * Typed envelope-aware fetch wrapper.
 *
 * All Calendar API calls go through here. The API uses a uniform error envelope
 * `{ error: { code, message, details } }` per PRD §"Error envelope" — we surface
 * that as a structured `ApiError` rather than a raw Response so callers can branch
 * on `.code` (e.g. `walkin_conflict`, `slot_taken`).
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Per-tenant slug; sent as X-Tenant-Slug to bind the request to a tenant schema. */
  tenantSlug?: string;
  /** Optional UMS bearer or local-JWT actor. Sent as X-Actor-Id (the API uses this for audit). */
  actorId?: string;
  /** Allows tests / SSR to plug in a fetch implementation. */
  fetcher?: typeof fetch;
  /**
   * Invoked once when any request returns 401. The handler typically clears the
   * stored session and redirects to /login. Per-tenant TenantProvider wires this
   * up; tests may pass undefined to opt out.
   */
  onUnauthorized?: () => void;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Override the tenant slug for this single call. */
  tenantSlug?: string;
}

export class ApiClient {
  baseUrl: string;
  tenantSlug?: string;
  actorId?: string;
  private readonly fetcher: typeof fetch;
  private readonly onUnauthorized?: () => void;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ?? "";
    this.tenantSlug = options.tenantSlug;
    this.actorId = options.actorId;
    this.fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = options.onUnauthorized;
  }

  withTenant(slug: string): ApiClient {
    return new ApiClient({
      baseUrl: this.baseUrl,
      tenantSlug: slug,
      actorId: this.actorId,
      fetcher: this.fetcher,
      onUnauthorized: this.onUnauthorized
    });
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    // Prepend the SPA's mount-path prefix so requests issued from
    // meetandtour.com/calendar/ land on /calendar/api/* (forwarded by
    // site-proxy → calendar API), not on /api/* (which the customer
    // domain doesn't route).
    const prefix = basePath();
    const fullPath = prefix && path.startsWith("/") ? `${prefix}${path}` : path;
    const url = new URL(fullPath, this.baseUrl || globalThis.location?.origin || "http://localhost");
    if (options.query) {
      for (const [key, raw] of Object.entries(options.query)) {
        if (raw === undefined || raw === null) continue;
        url.searchParams.set(key, String(raw));
      }
    }
    const headers: Record<string, string> = {};
    if (options.body !== undefined) {
      headers["content-type"] = "application/json";
    }
    // Bind the request to a tenant schema. The backend resolves the tenant from
    // the SSO cookie by default, but a superadmin (UMS Console admin) uses this
    // header to operate on a SELECTED tenant — it's how the sidebar tenant
    // switcher works (authenticateAndResolve honors x-tenant-slug for
    // superadmins; for every other principal the server ignores it and stays
    // pinned to the cookie's tenant, so sending it is always safe).
    const tenantSlug = options.tenantSlug ?? this.tenantSlug;
    if (tenantSlug) headers["x-tenant-slug"] = tenantSlug;
    if (this.actorId) headers["x-actor-id"] = this.actorId;
    if(!["GET","HEAD","OPTIONS"].includes(options.method||"GET"))headers["x-csrf-token"]=calendarCsrfToken();

    const response = await this.fetcher(url.toString(), {
      method: options.method ?? "GET",
      headers,
      // Calendar uses the SSO cookie session as identity; tenant + actor are
      // resolved server-side from the cookie. credentials:'include' ensures
      // the cookie ships on cross-origin XHR (calendar.ronic.ai vs the
      // tenant-domain mount point share the same backend pod but the SPA
      // origin can differ from the API origin during dev/proxy setups).
      credentials: "include",
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal
    });

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get("content-type") ?? "";
    const payload = contentType.includes("json") ? await response.json() : await response.text();

    if (!response.ok) {
      // 401 always triggers the onUnauthorized hook so the app can clear the
      // session + redirect — even before the caller's catch runs.
      if (response.status === 401 && this.onUnauthorized) {
        try {
          this.onUnauthorized();
        } catch {
          // Hook errors must never escape the request path.
        }
      }
      const env = (payload as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | string);
      if (typeof env === "object" && env?.error) {
        throw new ApiError(
          response.status,
          env.error.code ?? "unknown_error",
          env.error.message ?? response.statusText,
          env.error.details
        );
      }
      throw new ApiError(response.status, "http_error", `HTTP ${response.status}: ${response.statusText}`, {
        body: env
      });
    }
    return payload as T;
  }

  // ---------------- Auth & profile ----------------
  authMe(tenantSlug: string) {
    return this.request<AuthMe>("/api/v1/auth/me", { tenantSlug });
  }
  verticalProfile(tenantSlug: string) {
    return this.request<VerticalProfile>("/api/v1/vertical-profile", { tenantSlug });
  }

  // ---------------- Locations ----------------
  listLocations() {
    return this.request<{ data: Location[] }>("/api/v1/locations");
  }
  createLocation(body: Partial<Location>) {
    return this.request<Location>("/api/v1/locations", { method: "POST", body });
  }

  // ---------------- Resources ----------------
  listResources() {
    return this.request<{ data: Resource[] }>("/api/v1/resources");
  }
  createResource(body: Partial<Resource>) {
    return this.request<Resource>("/api/v1/resources", { method: "POST", body });
  }
  updateResource(id: string, body: Partial<Resource>) {
    return this.request<Resource>(`/api/v1/resources/${id}`, { method: "PATCH", body });
  }
  deleteResource(id: string) {
    return this.request<Resource>(`/api/v1/resources/${id}`, { method: "DELETE" });
  }
  getSchedule(resourceId: string) {
    return this.request<Schedule>(`/api/v1/resources/${resourceId}/schedule`);
  }
  putSchedule(resourceId: string, body: Schedule) {
    return this.request<Schedule>(`/api/v1/resources/${resourceId}/schedule`, { method: "PUT", body });
  }
  bulkScheduleOverrides(resourceId: string, body: BulkScheduleOverridesInput) {
    return this.request<BulkScheduleOverridesResult>(`/api/v1/resources/${resourceId}/schedule/bulk-overrides`, {
      method: "POST",
      body
    });
  }

  // ---------------- Services ----------------
  listServices() {
    return this.request<{ data: Service[] }>("/api/v1/services");
  }
  createService(body: Partial<Service>) {
    return this.request<Service>("/api/v1/services", { method: "POST", body });
  }
  updateService(id: string, body: Partial<Service>) {
    return this.request<Service>(`/api/v1/services/${id}`, { method: "PATCH", body });
  }
  deleteService(id: string) {
    return this.request<Service>(`/api/v1/services/${id}`, { method: "DELETE" });
  }

  // ---------------- Customers ----------------
  listCustomers() {
    return this.request<{ data: Customer[] }>("/api/v1/customers");
  }
  createCustomer(body: Partial<Customer> & { name: string }) {
    return this.request<Customer>("/api/v1/customers", { method: "POST", body });
  }
  updateCustomer(id: string, body: Partial<Customer>) {
    return this.request<Customer>(`/api/v1/customers/${id}`, { method: "PATCH", body });
  }
  deleteCustomer(id: string) {
    return this.request<Customer>(`/api/v1/customers/${id}`, { method: "DELETE" });
  }
  eraseCustomer(id: string) {
    return this.request<Customer>(`/api/v1/customers/${id}/erase`, { method: "POST" });
  }

  // ---------------- Appointments ----------------
  listAppointments(query: { from: string; to: string }) {
    return this.request<{ data: Appointment[] }>("/api/v1/appointments", { query });
  }
  createStaffAppointment(body: {
    customer_id: string;
    customer_name?: string;
    customer_phone?: string;
    service_id: string;
    primary_resource_id: string;
    helper_resource_ids?: string[];
    starts_at: string;
    notes?: string;
  }) {
    return this.request<Appointment>("/api/v1/appointments", { method: "POST", body });
  }
  createWalkin(body: {
    customer_id: string;
    customer_name?: string;
    customer_phone?: string;
    service_id: string;
    primary_resource_id: string;
    starts_at?: string;
    override_overlap?: boolean;
  }) {
    return this.request<Appointment>("/api/v1/appointments/walkin", { method: "POST", body });
  }
  rescheduleAppointment(id: string, body: { starts_at: string; primary_resource_id?: string }) {
    return this.request<Appointment>(`/api/v1/appointments/${id}/reschedule`, { method: "POST", body });
  }
  /**
   * PRD calendar-ux-parity (REQ-CUX-006): partial appointment update used for
   * drag-resize. The PRD wire body is `{ ends_at, idempotency_key }` — the
   * idempotency key prevents a duplicate PATCH on retry from double-committing.
   * Backend whitelists known fields (`ends_at`, `notes`, etc.) and accepts
   * `idempotency_key` without persisting it. If the resize would conflict with
   * another booking on the same resource the backend's GiST exclusion
   * surfaces `slot_taken` (status 409) which the caller handles with a
   * snap-back + toast (REQ-CUX-008).
   */
  patchAppointment(id: string, body: { ends_at?: string; notes?: string; idempotency_key?: string }) {
    return this.request<Appointment>(`/api/v1/appointments/${id}`, { method: "PATCH", body });
  }
  cancelAppointment(id: string, reason: string) {
    return this.request<Appointment>(`/api/v1/appointments/${id}/cancel`, {
      method: "POST",
      body: { reason }
    });
  }
  confirmAppointment(id: string) {
    return this.request<Appointment>(`/api/v1/appointments/${id}/confirm`, { method: "POST" });
  }
  noShowAppointment(id: string) {
    return this.request<Appointment>(`/api/v1/appointments/${id}/no-show`, { method: "POST" });
  }
  completeAppointment(id: string) {
    return this.request<Appointment>(`/api/v1/appointments/${id}/complete`, { method: "POST" });
  }

  // ---------------- Closures (PRD closures-and-holidays) ----------------
  /**
   * REQ-CL-002: list active closures, optionally constrained to a window.
   * Each row carries `occurrences_in_window` so the calendar grid can paint
   * closed-day overlays without re-running the rrule client-side.
   */
  listClosures(query?: { from?: string; to?: string }) {
    return this.request<{ data: TenantClosure[] }>("/api/v1/closures", { query });
  }
  createClosure(body: {
    reason: string;
    rrule?: string | null;
    dtstart: string;
    duration_minutes?: number;
    until?: string | null;
  }) {
    return this.request<TenantClosure>("/api/v1/closures", { method: "POST", body });
  }
  updateClosure(id: string, body: Partial<{
    reason: string;
    rrule: string | null;
    dtstart: string;
    duration_minutes: number;
    until: string | null;
  }>) {
    return this.request<TenantClosure>(`/api/v1/closures/${id}`, { method: "PATCH", body });
  }
  deleteClosure(id: string) {
    return this.request<TenantClosure>(`/api/v1/closures/${id}`, { method: "DELETE" });
  }
  importClosureSeed(seed: ClosureSeedName) {
    return this.request<{ created: number; ids: string[] }>("/api/v1/closures/import-seed", {
      method: "POST",
      body: { seed }
    });
  }

  // ---------------- Resource skills (PRD resource-skill-matrix) ----------------
  /**
   * REQ-RSM-006: list rows in the skill matrix. Default returns ALL rows
   * (active + expired + suspended) so the matrix UI can paint per-cell state.
   * Pass `active_only=true` to limit to rows that are currently bookable.
   */
  listResourceSkills(query?: { resource_id?: string; service_id?: string; active_only?: boolean }) {
    const stringQuery: Record<string, string> = {};
    if (query?.resource_id) stringQuery.resource_id = query.resource_id;
    if (query?.service_id) stringQuery.service_id = query.service_id;
    if (query?.active_only !== undefined) stringQuery.active_only = String(query.active_only);
    return this.request<{ data: ResourceSkill[]; next_cursor: string | null }>("/api/v1/resource-skills", { query: stringQuery });
  }
  createResourceSkill(body: {
    resource_id: string;
    service_id: string;
    proficiency?: Proficiency;
    valid_from?: string;
    valid_until?: string | null;
    jurisdiction?: string | null;
    credential_id?: string | null;
    credential_body?: string | null;
  }) {
    return this.request<ResourceSkill>("/api/v1/resource-skills", { method: "POST", body });
  }
  updateResourceSkill(id: string, body: {
    proficiency?: Proficiency;
    valid_from?: string;
    valid_until?: string | null;
    jurisdiction?: string | null;
    credential_id?: string | null;
    credential_body?: string | null;
  }) {
    return this.request<ResourceSkill>(`/api/v1/resource-skills/${id}`, { method: "PATCH", body });
  }
  deleteResourceSkill(id: string) {
    return this.request<{ id: string; deleted: true }>(`/api/v1/resource-skills/${id}`, { method: "DELETE" });
  }
  suspendResourceSkill(id: string, reason: string) {
    return this.request<ResourceSkill>(`/api/v1/resource-skills/${id}/suspend`, {
      method: "POST",
      body: { reason }
    });
  }
  resumeResourceSkill(id: string) {
    return this.request<ResourceSkill>(`/api/v1/resource-skills/${id}/resume`, {
      method: "POST",
      body: {}
    });
  }

  // ---------------- Recurrence ----------------
  listRecurrenceSeries() {
    return this.request<{ data: RecurrenceSeries[] }>("/api/v1/recurrence-series");
  }
  createRecurrenceSeries(body: {
    rrule: string;
    dtstart: string;
    customer_id: string;
    service_id: string;
    primary_resource_id: string;
    helper_resource_ids?: string[];
    until?: string;
    exdates?: string[];
  }) {
    return this.request<RecurrenceSeries>("/api/v1/recurrence-series", { method: "POST", body });
  }
  updateRecurrenceSeries(
    id: string,
    body: {
      scope: "this" | "this_and_future" | "all";
      rrule?: string;
      from_instance_appointment_id?: string;
    }
  ) {
    return this.request<RecurrenceSeries>(`/api/v1/recurrence-series/${id}`, { method: "PATCH", body });
  }
  cancelRecurrenceSeries(id: string) {
    return this.request<RecurrenceSeries>(`/api/v1/recurrence-series/${id}`, { method: "DELETE" });
  }

  // ---------------- Calendar sync ----------------
  listCalendarLinks() {
    return this.request<{ data: ExternalCalendarLink[] }>("/api/v1/calendar-sync/links");
  }
  /**
   * Returns the URL the browser should navigate to. The backend OAuth start
   * endpoint replies with a 302 to the provider; using `fetch` would make
   * `redirect: 'follow'` swallow that, so the caller hands the URL to
   * `window.location.href` directly. The frontend never expects a JSON body.
   */
  oauthStartUrl(provider: "google" | "outlook", resourceId: string, redirectUri: string): string {
    const params = new URLSearchParams({ resource_id: resourceId, redirect_uri: redirectUri });
    return `${this.baseUrl}/api/v1/calendar-sync/oauth/${provider}/start?${params.toString()}`;
  }
  deleteCalendarLink(id: string) {
    return this.request<{ removed: boolean }>(`/api/v1/calendar-sync/links/${id}`, { method: "DELETE" });
  }

  // ---------------- Sync conflicts ----------------
  listSyncConflicts() {
    return this.request<{ data: SyncConflict[] }>("/api/v1/sync-conflicts");
  }
  resolveSyncConflict(id: string, resolution: "accept" | "reject") {
    return this.request<SyncConflict>(`/api/v1/sync-conflicts/${id}/resolve`, {
      method: "POST",
      body: { resolution }
    });
  }

  // ---------------- Webhooks ----------------
  listWebhookSubscriptions() {
    return this.request<{ data: WebhookSubscription[] }>("/api/v1/webhook-subscriptions");
  }
  createWebhookSubscription(body: { url: string; event_types: string[]; secret?: string }) {
    return this.request<WebhookSubscription>("/api/v1/webhook-subscriptions", { method: "POST", body });
  }
  updateWebhookSubscription(id: string, body: Partial<{ event_types: string[]; state: "active" | "dead" }>) {
    return this.request<WebhookSubscription>(`/api/v1/webhook-subscriptions/${id}`, {
      method: "PATCH",
      body
    });
  }
  deleteWebhookSubscription(id: string) {
    return this.request<{ deleted: boolean }>(`/api/v1/webhook-subscriptions/${id}`, {
      method: "DELETE"
    });
  }
  testWebhookSubscription(id: string) {
    return this.request<{ queued: boolean }>(`/api/v1/webhook-subscriptions/${id}/test`, {
      method: "POST"
    });
  }

  // ---------------- Dead-letter queue ----------------
  listDeadLetterDeliveries() {
    return this.request<{ data: WebhookDelivery[] }>("/api/v1/webhook-deliveries", {
      query: { state: "dead_letter" }
    });
  }
  getWebhookDelivery(id: string) {
    return this.request<WebhookDelivery>(`/api/v1/webhook-deliveries/${id}`);
  }
  replayWebhookDelivery(id: string) {
    return this.request<WebhookDelivery>(`/api/v1/webhook-deliveries/${id}/replay`, {
      method: "POST"
    });
  }

  // ---------------- Audit & dispatch ----------------
  auditLog() {
    return this.request<{ data: AuditRecord[]; operator: OperatorAuditRecord[] }>("/api/v1/audit-log");
  }
  dispatchRoute(query: { resource_id: string; date: string }) {
    return this.request<{ data: DispatchStop[] }>("/api/v1/dispatch/route", { query });
  }

  // ---------------- Holds (read-only views) ----------------
  // No GET /holds endpoint; holds are surfaced via banners on appointments.

  // ---------------- Misc ----------------
  pingHealth() {
    return this.request<{ ok: boolean }>("/api/v1/auth/me").then(
      () => ({ ok: true }),
      () => ({ ok: false })
    );
  }
}

/**
 * Re-export PaginatedResponse so consumers don't have to deep-import.
 */
export type { PaginatedResponse, Hold };
